/* eslint-disable @typescript-eslint/no-this-alias */
import { DateTime } from 'luxon';
import videojs from 'video.js';

/** Inline SVG for camera icon (replaces FontAwesome fa-camera). Size 1.5em for better visibility in the control bar. */
const CAMERA_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="1.5em" height="1.5em"><path d="M12 12m-3.2 0a3.2 3.2 0 1 0 6.4 0 3.2 3.2 0 1 0 -6.4 0"/><path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/></svg>';

/** Minimal type for Video.js player instance (plugin runs with player as `this`). */
interface VideoJsPlayerInstance {
  el(): HTMLElement;
  trigger(event: string, data?: string): void;
  ready(cb: () => void): void;
  controlBar: {
    removeChild(name: string): void;
    addChild(name: string, opts: object, index: number): void;
    children(): unknown[];
  };
}

/** Options for Video.js component constructors (children, el, etc.). */
interface ComponentOptions {
  children?: unknown[];
  createEl?: boolean;
  el?: HTMLElement;
  id?: string;
}

function snapshotPlugin(this: VideoJsPlayerInstance) {
  const player: VideoJsPlayerInstance = this;
  const Button = videojs.getComponent('Button');

  class SnapshotButton extends Button {
    constructor(player: VideoJsPlayerInstance, options?: ComponentOptions) {
      // @ts-expect-error - VideoJsPlayerInstance is a minimal type; Button expects full videojs.Player
      super(player, options);
      // @ts-expect-error - controlText exists on Button at runtime but not in typed interface
      this.controlText('Take Snapshot');
      this.addClass('vjs-snapshot-button');
      const iconElement = document.createElement('span');
      iconElement.innerHTML = CAMERA_ICON_SVG;
      iconElement.classList.add('vjs-snapshot-icon');
      this.el().appendChild(iconElement);
    }

    handleClick() {
      const videoElement = player.el().getElementsByTagName('video')[0];
      if (!videoElement) {
        return;
      }

      const name = videoElement.dataset.stationName;
      const time = videoElement.dataset.timeFrom;
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;

      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      }

      const dataUrl = canvas.toDataURL('image/png');

      // Create a link element
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${name}_${DateTime.fromMillis(Number(time)).toFormat('yyyy-MM-dd_HH-mm-ss')}.png`;

      // Append link to the body
      document.body.appendChild(link);

      // Trigger a click on the link to download the image
      link.click();

      // Remove the link from the document
      document.body.removeChild(link);

      // Trigger the snapshotTaken event
      player.trigger('snapshotTaken', dataUrl);
    }
  }

  // Register the new button component
  // @ts-expect-error - SnapshotButton constructor signature is not assignable to registerComponent's Component type
  videojs.registerComponent('SnapshotButton', SnapshotButton);

  // Add the button to the control bar
  player.ready(() => {
    player.controlBar.removeChild('SnapshotButton');
    player.controlBar.addChild('SnapshotButton', {}, player.controlBar.children().length - 1);
  });
}

// Register the plugin with Video.js
videojs.registerPlugin('snapshotPlugin', snapshotPlugin);
